import { useEffect } from 'react';

/**
 * useScrollReveal
 *
 * Attaches an IntersectionObserver to every element with the .reveal class
 * inside the document and, on intersection, adds .reveal-in so the element
 * fades + slides into place. Used app-wide so any element can opt-in to a
 * soft scroll-in animation by simply adding `className="reveal"`.
 *
 * It also re-scans the DOM whenever the route changes (`pathname` dep) so
 * newly mounted pages pick up their reveals.
 */
export const useScrollReveal = (pathname) => {
    useEffect(() => {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;

        // Reset any reveal-in tags so the new page replays its animation
        const all = document.querySelectorAll('.reveal');
        all.forEach((el) => el.classList.remove('reveal-in'));

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('reveal-in');
                        io.unobserve(entry.target);
                    }
                });
            },
            { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
        );

        // Defer so the page has time to paint its first frame
        const timer = setTimeout(() => {
            document.querySelectorAll('.reveal:not(.reveal-in)').forEach((el) => io.observe(el));
        }, 50);

        return () => {
            clearTimeout(timer);
            io.disconnect();
        };
    }, [pathname]);
};

export default useScrollReveal;
